import { useContext, useEffect } from 'react';
import { RemoteDataContext } from '../../remote-data/RemoteDataContext';

const useRemoteData = (f, [...effectChanges]) => {
  const context = useContext(RemoteDataContext);
  const { key, onInitialFetch, initialState } = f();

  if (!context) {
    throw Error('useRemoteData need to be used under the <RemoteDataProvider />');
  }

  const {
    operations: {
      useRemoteData: fetchRemoteDataOperation,
      invalidateRemoteData,
      updateEntity,
      createEntity,
      partialCreateEntity,
      partialUpdateEntity,
    },
    selectors: { useGetSync },
  } = context;

  useEffect(() => {
    // THIS IS VERY IMPORTANT: ONLY WHEN ALL PARAMETERS ARE DEFINED WE CAN FETCH DATA
    // this is so we only fetch data when is valid and we do not leak any not needed data to the context.
    if (!effectChanges.some((value) => !value) || effectChanges.length === 0) {
      fetchRemoteDataOperation(key, onInitialFetch, initialState);
    }
  }, [fetchRemoteDataOperation, ...effectChanges]);

  const { data, loading, failed, fetched, ex } = useGetSync(key, initialState);

  const doFetch = () => {
    invalidateRemoteData(key);
    fetchRemoteDataOperation(key, onInitialFetch, initialState);
  };

  const createOrUpdateEntity = (entity) => {
    // this will create the entity is it is not present already
    createEntity(entity, { keys: [key] });
    // this will update it if it is already created
    updateEntity({ ...entity, updatedAt: new Date().toISOString() });
  };

  const partialCreateOrUpdateEntity = (entity) => {
    // this will create the entity is it is not present already
    partialCreateEntity({ ...entity, createdAt: undefined });
    // this will update it if it is already created
    partialUpdateEntity({ ...entity });
  };

  const invalidateKey = () => {
    invalidateRemoteData(key);
  };

  return [
    { data, loading, failed, fetched, ex },
    {
      doFetch,
      createOrUpdateEntity,
      partialCreateOrUpdateEntity,
      invalidateKey,
    },
  ];
};

export default useRemoteData;
